///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/components/ui/use-toast'
import {
  useAdminDeleteGeoPackageMutation,
  useAdminListGeoPackagesQuery,
  useGetGeoPackageStatsQuery,
} from '@/redux/api/geopackageApi'
import type { GeoPackageCatalog, PackageStatus } from '@shared-types/geopackage-catalog'
import { Database, Download, HardDrive, Package, Trash2 } from 'lucide-react'
import { useState } from 'react'

const PAGE_SIZE = 20

const statusStyles: Record<PackageStatus, string> = {
  active: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
  deprecated: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20',
  archived: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-500/20',
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function StatTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div className="p-2 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400">
        {icon}
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold">{value}</p>
      </div>
    </div>
  )
}

export function GeoPackagesSection() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [page, setPage] = useState(0)
  const [packageToDelete, setPackageToDelete] = useState<GeoPackageCatalog | null>(null)

  const { data, isLoading, error } = useAdminListGeoPackagesQuery({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  })
  const { data: stats } = useGetGeoPackageStatsQuery()
  const [deletePackage, { isLoading: isDeleting }] = useAdminDeleteGeoPackageMutation()

  const handleDelete = async () => {
    if (!packageToDelete) return
    try {
      await deletePackage(packageToDelete.id).unwrap()
      toast({
        title: 'GeoPackage deleted',
        description: `"${packageToDelete.name}" has been removed from the catalog.`,
      })
      setPackageToDelete(null)
    } catch (error: unknown) {
      const errorMessage = error && typeof error === 'object' && 'data' in error &&
        error.data && typeof error.data === 'object' && 'message' in error.data
        ? String(error.data.message)
        : 'Failed to delete GeoPackage'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/70 bg-red-500/10 p-6 text-center">
        <p className="font-semibold text-red-600 dark:text-red-400">Error loading GeoPackages</p>
        <p className="text-sm mt-2 text-muted-foreground">Please check your permissions and try again.</p>
      </div>
    )
  }

  const packages = data?.packages ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-5">
      {stats && (
        <div className="grid gap-3 sm:grid-cols-3">
          <StatTile
            icon={<Package className="h-4 w-4" />}
            label="Packages"
            value={String(stats.total_packages)}
          />
          <StatTile
            icon={<Download className="h-4 w-4" />}
            label="Downloads"
            value={stats.total_downloads.toLocaleString()}
          />
          <StatTile
            icon={<HardDrive className="h-4 w-4" />}
            label="Total Size"
            value={formatBytes(stats.total_size_bytes)}
          />
        </div>
      )}

      <div className="flex items-center gap-4">
        <Select
          value={statusFilter}
          onValueChange={(value: string) => {
            setStatusFilter(value)
            setPage(0)
          }}
        >
          <SelectTrigger className="w-[180px] rounded-xl focus:ring-2 focus:ring-blue-500" aria-label="Filter by status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all" className="rounded-lg">All Statuses</SelectItem>
            <SelectItem value="active" className="rounded-lg">Active</SelectItem>
            <SelectItem value="deprecated" className="rounded-lg">Deprecated</SelectItem>
            <SelectItem value="archived" className="rounded-lg">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : packages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 flex flex-col items-center justify-center text-center">
          <div className="p-3 rounded-full bg-teal-500/10 mb-4">
            <Database className="h-8 w-8 text-teal-500" />
          </div>
          <h3 className="text-base font-semibold mb-1">No GeoPackages found</h3>
          <p className="text-sm text-muted-foreground">
            {statusFilter !== 'all'
              ? 'Try a different status filter.'
              : 'Uploaded packages will appear here once added to the catalog.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Package</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead className="text-right">Downloads</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{pkg.name}</span>
                        <code className="font-mono text-xs text-muted-foreground">{pkg.package_id}</code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-full capitalize">{pkg.category}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{pkg.version}</TableCell>
                    <TableCell className="text-right text-xs">{formatBytes(pkg.file_size)}</TableCell>
                    <TableCell className="text-right text-xs">{pkg.download_count.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full capitalize ${statusStyles[pkg.status] ?? statusStyles.archived}`}>
                        {pkg.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs capitalize text-muted-foreground">{pkg.visibility}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setPackageToDelete(pkg)}
                        disabled={isDeleting}
                        aria-label={`Delete ${pkg.name}`}
                        className="hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, total)} of {total} packages
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Previous
                </Button>
                <span className="text-sm px-2">
                  Page {page + 1} of {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page + 1 >= pageCount}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <AlertDialog open={packageToDelete !== null} onOpenChange={(open) => !open && setPackageToDelete(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete GeoPackage
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{packageToDelete?.name}</strong>{' '}
              (<code className="font-mono">{packageToDelete?.package_id}</code>)?
              This removes it from the catalog for all users and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-red-600 hover:bg-red-700 transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-red-500 disabled:hover:scale-100"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
